import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import type { SchoolClassroomLoginHistory } from '@/core/domain/school/classroom/SchoolClassroomLoginHistory.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import FetchSchoolClassroomLoginHistoryUseCase
  from '@/core/useCases/dashboard/FetchSchoolClassroomLoginHistoryUseCase.ts'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import type SchoolClassroom from '@/core/domain/school/classroom/SchoolClassroom.ts'
import { useDashboardStore } from '@/presentation/stores/dashboard/dashboard.ts'
import { FetchSchoolClassroomUseCase } from '@/core/useCases/school/classroom/FetchSchoolClassroomUseCase.ts'
import {
  type DashboardClassroomDiagnosticTrigger,
  DashboardClassroomDiagnosticTriggerInput
} from '@/core/domain/dashboard/DashboardClassroomDiagnosticTrigger.ts'
import {
  FetchAllDashboardClassroomDiagnosticTriggerUseCase
} from '@/core/useCases/dashboard/FetchAllDashboardClassroomDiagnosticTriggerUseCase.ts'
import {
  CreateDashboardClassroomDiagnosticTriggerUseCase
} from '@/core/useCases/dashboard/CreateDashboardClassroomDiagnosticTriggerUseCase.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { type AssignmentForClassroom } from '@/core/domain/school/assignment/AssignmentForClassroom'
import {
  FetchAssignmentsForClassroomUseCase
} from '@/core/useCases/school/assignment/FetchAssignmentsForClassroomUseCase'
import { FetchClassroomDiagnosticsUseCase } from '@/core/useCases/dashboard/FetchClassroomDiagnosticsUseCase'
import { FetchClassroomDiagnosticStateUseCase } from '@/core/useCases/dashboard/FetchClassroomDiagnosticStateUseCase'
import { TriggerDiagnosticStartUseCase } from '@/core/useCases/dashboard/TriggerDiagnosticStartUseCase'
import { TriggerDiagnosticEndUseCase } from '@/core/useCases/dashboard/TriggerDiagnosticEndUseCase'
import type { DashboardClassroomDiagnosticState } from '@/core/domain/dashboard/DashboardClassroomDiagnosticState'
import { SetModuleVisibilityUseCase } from '@/core/useCases/school/assignment/SetModuleVisibilityUseCase.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'
import { FetchQuizVersionByIdUseCase } from '@/core/useCases/quiz/FetchQuizVersionByIdUseCase.ts'

export const useDashboardClassroomStore = defineStore('dashboardClassroom', () => {
  // BASE COMMUNE
  const baseStore = createStoreBase([
    'fetchLoginHistory',
    'setClassroom',
    'classroomDiagnosticTriggers',
    'fetchClassroomAssignments',
    'fetchClassroomDiagnostics',
    'publishClassroomDiagnostic',
    'buildDiagnosticsForClassroom'
  ])

  // ÉTAT
  const dashboardClassroomLoginHistory: Ref<SchoolClassroomLoginHistory | null> = ref(null)
  const dashboardSelectedClassroom: Ref<SchoolClassroom | null> = ref(null)
  const dashboardClassroomDiagnosticTriggers: Ref<DashboardClassroomDiagnosticTrigger[] | null> =
    ref(null)
  const dashboardClassroomAssignments: Ref<AssignmentForClassroom | null> = ref(null)
  const dashboardClassroomDiagnosticState: Ref<DashboardClassroomDiagnosticState | null> = ref(null)
  const contextSchoolId: Ref<number | null> = ref(null)
  const contextPeriodId: Ref<number | null> = ref(null)

  const dashboardClassroomDiagnosticsByType = ref<Record<QuizType.Values.DIAG_START|QuizType.Values.DIAG_END, QuizVersion[]>>({
    DIAG_END: [],
    DIAG_START: []
  })


  // ACTIONS
  /**
   * Récupère l'historique de connexion pour la classe sélectionnée
   * @param forceRefresh Force le rechargement des données même si elles sont récentes
   */
  async function fetchDashboardClassroomLoginHistory(
    forceRefresh = false
  ): Promise<SchoolClassroomLoginHistory | null> {
    // Éviter le rechargement inutile si les données sont déjà là et récentes
    if (
      !forceRefresh &&
      dashboardClassroomLoginHistory.value &&
      baseStore.lastUpdated.value &&
      Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 // 5 minutes
    ) {
      return dashboardClassroomLoginHistory.value
    }

    try {
      baseStore.loading.value.fetchLoginHistory = true
      baseStore.resetError()

      const schoolClassroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      const schoolId = contextSchoolId.value
      const periodId = contextPeriodId.value

      if (!schoolClassroomId || !schoolId || !periodId) {
        return Promise.reject(
          new Error("Informations manquantes pour récupérer l'historique de connexion")
        )
      }

      const result = await DependencyContainer.resolve(
        FetchSchoolClassroomLoginHistoryUseCase
      ).execute(schoolId, periodId, schoolClassroomId)

      if (result) {
        dashboardClassroomLoginHistory.value = result
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error("Erreur lors de la récupération de l'historique de connexion:", err)
      return null
    } finally {
      baseStore.loading.value.fetchLoginHistory = false
    }
  }

  /**
   * Définit la classe sélectionnée dans le dashboard et met à jour le contexte
   * @param classroom Classe à sélectionner
   * @param schoolId ID de l'école associée
   * @param periodId ID de la période associée
   */
  function setDashboardSelectedClassroom(
    classroom: SchoolClassroom,
    schoolId: number,
    periodId: number
  ): void {
    // Vérifier si la classe est déjà sélectionnée
    if (dashboardSelectedClassroom.value?.schoolClassroomId === classroom.schoolClassroomId) {
      return
    }

    try {
      baseStore.loading.value.setClassroom = true
      baseStore.resetError()

      dashboardSelectedClassroom.value = classroom
      contextSchoolId.value = schoolId
      contextPeriodId.value = periodId
      baseStore.lastUpdated.value = new Date()
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la définition de la classe sélectionnée:', err)
    } finally {
      baseStore.loading.value.setClassroom = false
    }
  }

  /**
   * Définit la classe sélectionnée dans le dashboard par son ID
   * @param schoolId ID de l'école
   * @param classroomId ID de la classe
   * @returns Promise qui se résout quand l'opération est terminée
   */
  async function setDashboardSelectedClassroomById(
    schoolId: number,
    classroomId: number
  ): Promise<void> {
    // Si la classe est déjà sélectionnée, ne rien faire
    if (
      dashboardSelectedClassroom.value?.schoolClassroomId === classroomId &&
      contextSchoolId.value === schoolId
    ) {
      console.log('Classe déjà sélectionnée, aucune mise à jour nécessaire')
      return Promise.resolve()
    }

    try {
      baseStore.loading.value.setClassroom = true
      baseStore.resetError()

      // Obtenir le dashboard store pour récupérer le periodId
      const dashboardStore = useDashboardStore()

      // Définir l'école sélectionnée par ID dans le dashboard store
      await dashboardStore.setDashboardSelectedSchoolById(schoolId)

      // Récupérer l'école sélectionnée
      const selectedSchool = dashboardStore.dashboardSelectedSchool
      if (!selectedSchool) {
        throw new Error(`École avec l'ID ${schoolId} non trouvée`)
      }

      const periodId = selectedSchool.schoolPeriodId

      // Récupérer la classe complète via le UseCase
      const classroom = await DependencyContainer.resolve(FetchSchoolClassroomUseCase).execute(
        schoolId,
        periodId,
        classroomId
      )

      if (classroom) {
        dashboardSelectedClassroom.value = classroom
        contextSchoolId.value = schoolId
        contextPeriodId.value = periodId
        baseStore.lastUpdated.value = new Date()
        return Promise.resolve()
      } else {
        throw new Error(`Classe avec l'ID ${classroomId} non trouvée`)
      }
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la définition de la classe sélectionnée:', err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.setClassroom = false
    }
  }

  async function buildDiagnosticsForClassroom(): Promise<void> {
    try {
      baseStore.loading.value.buildDiagnosticsForClassroom = true
      baseStore.resetError()

      if (dashboardClassroomDiagnosticState.value?.diagStartQuizVersionIds) {
        dashboardClassroomDiagnosticsByType.value[QuizType.Values.DIAG_START] = await Promise.all(
          dashboardClassroomDiagnosticState.value?.diagStartQuizVersionIds.map((quizVersionId) =>
            DependencyContainer.resolve(FetchQuizVersionByIdUseCase).execute(quizVersionId)
          )
        )
      }

      if (dashboardClassroomDiagnosticState.value?.diagEndQuizVersionIds) {
        dashboardClassroomDiagnosticsByType.value[QuizType.Values.DIAG_END] = await Promise.all(
          dashboardClassroomDiagnosticState.value?.diagEndQuizVersionIds.map((quizVersionId) =>
            DependencyContainer.resolve(FetchQuizVersionByIdUseCase).execute(quizVersionId)
          )
        )
      }
    } catch (error) {
      baseStore.setError(error)
    } finally {
      baseStore.loading.value.buildDiagnosticsForClassroom = false
    }
  }

  /**
   * Récupère l'ensemble des Triggers des Diagnostics pour la classe sélectionnée
   */
  async function fetchDashboardClassroomDiagnosticTriggers(): Promise<
    DashboardClassroomDiagnosticTrigger[] | null
  > {
    try {
      baseStore.loading.value.classroomDiagnosticTriggers = true
      baseStore.resetError()

      const schoolClassroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      if (!schoolClassroomId) {
        return Promise.reject(new Error('Informations manquantes pour récupérer les triggers'))
      }

      const result = await DependencyContainer.resolve(
        FetchAllDashboardClassroomDiagnosticTriggerUseCase
      ).execute(schoolClassroomId)

      if (result) {
        dashboardClassroomDiagnosticTriggers.value = result
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la récupération des triggers:', err)
      return null
    } finally {
      baseStore.loading.value.classroomDiagnosticTriggers = false
    }
  }

  async function createDashboardClassroomDiagnosticTriggers(
    quizType: QuizType.Values,
    learningCourseName: string,
    triggerTime: Date
  ): Promise<DashboardClassroomDiagnosticTrigger[] | null> {
    try {
      baseStore.loading.value.classroomDiagnosticTriggers = true
      baseStore.resetError()

      const schoolClassroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      if (!schoolClassroomId) {
        return Promise.reject(new Error('Informations manquantes pour créer les triggers'))
      }

      const input = DashboardClassroomDiagnosticTriggerInput.create({
        id: 0,
        schoolClassroomId: schoolClassroomId,
        learningCourseName: learningCourseName,
        quizType: quizType,
        triggerTime: triggerTime.toISOString()
      })

      const results = await DependencyContainer.resolve(
        CreateDashboardClassroomDiagnosticTriggerUseCase
      ).execute(schoolClassroomId, input)

      if (results) {
        dashboardClassroomDiagnosticTriggers.value = (
          dashboardClassroomDiagnosticTriggers.value ?? []
        ).concat(results)
        baseStore.lastUpdated.value = new Date()
      }
      return results
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la création des triggers:', err)
      return null
    } finally {
      baseStore.loading.value.classroomDiagnosticTriggers = false
    }
  }

  /**
   * Récupère les diagnostics disponibles pour la classe sélectionnée
   */
  async function fetchDashboardClassroomDiagnostics(
    forceRefresh = false
  ): Promise<DashboardClassroomDiagnosticTrigger[] | null> {
    try {
      baseStore.loading.value.fetchClassroomDiagnostics = true
      baseStore.resetError()

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      if (!classroomId) {
        return Promise.reject(new Error('Informations manquantes pour récupérer les diagnostics'))
      }

      return await DependencyContainer.resolve(FetchClassroomDiagnosticsUseCase).execute(
        classroomId
      )
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la récupération des diagnostics:', err)
      return null
    } finally {
      baseStore.loading.value.fetchClassroomDiagnostics = false
    }
  }

  /**
   * Récupère l'état des diagnostics pour la classe sélectionnée
   */
  async function fetchDashboardClassroomDiagnosticState(): Promise<DashboardClassroomDiagnosticState | null> {
    try {
      baseStore.loading.value.fetchClassroomDiagnostics = true
      baseStore.resetError()

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      if (!classroomId) {
        return Promise.reject(
          new Error("Informations manquantes pour récupérer l'état des diagnostics")
        )
      }

      const result = await DependencyContainer.resolve(
        FetchClassroomDiagnosticStateUseCase
      ).execute(classroomId)

      if (result) {
        dashboardClassroomDiagnosticState.value = result
        await buildDiagnosticsForClassroom()
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error("Erreur lors de la récupération de l'état des diagnostics:", err)
      return null
    } finally {
      baseStore.loading.value.fetchClassroomDiagnostics = false
    }
  }

  /**
   * Déclenche le diagnostic T1 (start) pour la classe sélectionnée
   */
  async function triggerDashboardDiagnosticStart(): Promise<
    DashboardClassroomDiagnosticTrigger[] | null
  > {
    try {
      baseStore.loading.value.publishClassroomDiagnostic = true
      baseStore.resetError()

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      if (!classroomId) {
        return Promise.reject(new Error('Informations manquantes pour déclencher le diagnostic'))
      }

      const result = await DependencyContainer.resolve(TriggerDiagnosticStartUseCase).execute(
        classroomId,
        classroomId
      )

      if (result) {
        // Mettre à jour les triggers existants
        dashboardClassroomDiagnosticTriggers.value = (
          dashboardClassroomDiagnosticTriggers.value ?? []
        ).concat(result)
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors du déclenchement du diagnostic T1:', err)
      return null
    } finally {
      baseStore.loading.value.publishClassroomDiagnostic = false
    }
  }

  /**
   * Déclenche le diagnostic T2 (end) pour la classe sélectionnée
   */
  async function triggerDashboardDiagnosticEnd(): Promise<
    DashboardClassroomDiagnosticTrigger[] | null
  > {
    try {
      baseStore.loading.value.publishClassroomDiagnostic = true
      baseStore.resetError()

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      if (!classroomId) {
        return Promise.reject(new Error('Informations manquantes pour déclencher le diagnostic'))
      }

      const result = await DependencyContainer.resolve(TriggerDiagnosticEndUseCase).execute(
        classroomId,
        classroomId
      )

      if (result) {
        // Mettre à jour les triggers existants
        dashboardClassroomDiagnosticTriggers.value = (
          dashboardClassroomDiagnosticTriggers.value ?? []
        ).concat(result)
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors du déclenchement du diagnostic T2:', err)
      return null
    } finally {
      baseStore.loading.value.publishClassroomDiagnostic = false
    }
  }

  /**
   * Récupère les assignments (configuration des modules) pour la classe sélectionnée
   */
  async function fetchDashboardClassroomAssignments(
    forceRefresh = false
  ): Promise<AssignmentForClassroom | null> {
    // Éviter le rechargement inutile si les données sont déjà là et récentes
    if (
      !forceRefresh &&
      dashboardClassroomAssignments.value &&
      baseStore.lastUpdated.value &&
      Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 // 5 minutes
    ) {
      return dashboardClassroomAssignments.value
    }

    try {
      baseStore.loading.value.fetchClassroomAssignments = true
      baseStore.resetError()

      const schoolClassroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      const schoolId = contextSchoolId.value
      const periodId = contextPeriodId.value

      if (!schoolClassroomId || !schoolId || !periodId) {
        return Promise.reject(
          new Error('Informations manquantes pour récupérer la configuration des modules')
        )
      }

      const result = await DependencyContainer.resolve(FetchAssignmentsForClassroomUseCase).execute(
        schoolId,
        periodId,
        schoolClassroomId
      )

      if (result) {
        dashboardClassroomAssignments.value = result
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      // Une vraie erreur s'est produite (autre que "pas d'assignment")
      baseStore.setError(err)
      console.error('Erreur lors de la récupération de la configuration des modules:', err)
      dashboardClassroomAssignments.value = null
      return null
    } finally {
      baseStore.loading.value.fetchClassroomAssignments = false
    }
  }

  /**
   * Rend visible un module pour les élèves
   */
  async function setModuleVisibility(
    schoolId: number,
    periodId: number,
    assignmentId: number,
    moduleId: number,
    classroomId: number
  ): Promise<void> {
    try {
      baseStore.loading.value.setModuleVisibility = true
      baseStore.resetError()

      const response = await DependencyContainer.resolve(SetModuleVisibilityUseCase).execute(
        schoolId,
        periodId,
        assignmentId,
        moduleId,
        classroomId
      )

      // Mettre à jour dans les données du store
      const index = dashboardClassroomAssignments.value?.assignedModules.findIndex((module) => module.id === response.schoolAssignedModuleId)
      if (index && index !== -1 && dashboardClassroomAssignments.value?.assignedModules[index]) {
        dashboardClassroomAssignments.value.assignedModules[index] =
          dashboardClassroomAssignments.value?.assignedModules[index].copy({
            order: response.order,
            visible: response.visible,
            visibleAt: response.visibleAt
          })
      }
      baseStore.updateLastUpdated()
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.setModuleVisibility = false
    }
  }


  /**
   * Réinitialise l'état du store
   */
  function reset(): void {
    dashboardClassroomLoginHistory.value = null
    dashboardSelectedClassroom.value = null
    dashboardClassroomDiagnosticTriggers.value = null
    dashboardClassroomAssignments.value = null
    dashboardClassroomDiagnosticState.value = null
    dashboardClassroomDiagnosticsByType.value = { DIAG_START: [], DIAG_END: [] }
    contextSchoolId.value = null
    contextPeriodId.value = null
    baseStore.lastUpdated.value = null
    baseStore.resetError()

    baseStore.loading.value = {
      fetchLoginHistory: false,
      setClassroom: false,
      classroomDiagnosticTriggers: false,
      fetchClassroomAssignments: false,
      fetchClassroomDiagnostics: false,
      publishClassroomDiagnostic: false
    }
  }

  // GETTERS

  return {
    // État
    dashboardSelectedClassroom,
    dashboardClassroomLoginHistory,
    dashboardClassroomDiagnosticTriggers,
    dashboardClassroomAssignments,
    dashboardClassroomDiagnosticState,
    dashboardClassroomDiagnosticsByType,
    dashboardClassroomStoreLoading: baseStore.loading,
    dashboardClassroomStoreError: baseStore.error,
    baseStoreLastUpdated: baseStore.lastUpdated,
    contextSchoolId,
    contextPeriodId,

    // Actions
    setDashboardSelectedClassroom,
    setDashboardSelectedClassroomById,
    fetchDashboardClassroomLoginHistory,
    fetchDashboardClassroomDiagnosticTriggers,
    createDashboardClassroomDiagnosticTriggers,
    fetchDashboardClassroomAssignments,
    setModuleVisibility,
    fetchDashboardClassroomDiagnostics,
    fetchDashboardClassroomDiagnosticState,
    triggerDashboardDiagnosticStart,
    triggerDashboardDiagnosticEnd,
    reset,

    // Getters
    showToast: baseStore.showToast,
    dashboardClassroomStoreIsLoading: baseStore.isLoading,
    classroomDiagnosticTriggerIsLoading: baseStore.loading.value.classroomDiagnosticTriggers,
    classroomAssignmentsIsLoading: baseStore.loading.value.fetchClassroomAssignments,
    needsFreshData: baseStore.shouldRefreshData
  }
})
